import React, { useState, useEffect } from 'react'
import api from '../api/axios'
import './Crafting.css'

type CraftDef = {
  category: 'weapon' | 'equipment' | 'tools'
  baseRecipe?: Record<string, number>
  baseStats?: Record<string, number>
  upgradeIncr?: Record<string, number>
  upgradable: boolean
}

type CraftDefs = Record<string, CraftDef>

export default function CraftingPage() {
  const [defs, setDefs] = useState<CraftDefs | null>(null)
  const [inventory, setInventory] = useState<any>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'craft'|'upgrade'>('craft')

  useEffect(() => {
    Promise.all([
      api.get<CraftDefs>('/crafting/definitions'),
      api.get<any>('/character')
    ])
    .then(([defsRes, charRes]) => {
      setDefs(defsRes.data)
      setInventory(charRes.data.inventory)
    })
    .catch(err => {
      console.error(err)
      setMsg('Failed to load crafting data.')
    })
  }, [])

  const displayName = (key: string) =>
    key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

  const craft = async (key: string) => {
    setMsg(null)
    try {
      await api.post('/crafting', { itemKey: key })
      setMsg(`Crafted ${displayName(key)}!`)
      const char = await api.get<any>('/character')
      setInventory(char.data.inventory)
    } catch (e: any) {
      setMsg(e.response?.data?.error || 'Craft failed')
    }
  }

  const upgrade = async (key: string) => {
    setMsg(null)
    try {
      await api.post('/upgrade', { itemKey: key })
      setMsg(`Upgraded ${displayName(key)}!`)
      const char = await api.get<any>('/character')
      setInventory(char.data.inventory)
    } catch (e: any) {
      setMsg(e.response?.data?.error || 'Upgrade failed')
    }
  }

  let craftList: [string,CraftDef][] = []
  let upgradeList: [string,CraftDef][] = []

  if (defs && inventory) {
    craftList = Object.entries(defs)
      .filter(([k, def]) =>
        def.category !== 'tools' &&
        !inventory[def.category]?.[k]
      )

    upgradeList = Object.entries(defs)
      .filter(([k, def]) => {
        const owned = inventory[def.category]?.[k] ||
          (def.category === 'tools' && inventory.tools?.[k])
        return def.upgradable && owned
      })
  }

  const imgFor = (key: string) => {
    try {
      // assumes images are in src/assets/crafting/<key>.png
      return require(`../assets/crafting/${key}.png`)
    } catch {
      return require(`../assets/crafting/placeholder.png`)
    }
  }

  return (
    <div className="crafting-wrapper">
      <div className="crafting-page">
        <h2>Crafting &amp; Upgrades</h2>

        {(!defs || !inventory)
          ? <p className="loading">Loading crafting…</p>
          : (
          <>
            {msg && (
              <p className={msg.toLowerCase().includes('fail') ? 'craft-msg error' : 'craft-msg success'}>
                {msg}
              </p>
            )}

            <div className="craft-tab-bar">
              {(['craft','upgrade'] as const).map(tab => (
                <button
                  key={tab}
                  className={`craft-tab-btn${activeTab === tab ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === 'craft' ? 'Craft' : 'Upgrade'}
                </button>
              ))}
            </div>

            {activeTab === 'craft' && (
              <ul className="craft-list">
                {craftList.map(([key, def]) => {
                  const recipe = def.baseRecipe ? Object.entries(def.baseRecipe) : []
                  const stats  = def.baseStats ? Object.entries(def.baseStats) : []
                  return (
                    <li key={key}>
                      <div className="item-header">
                        <img src={imgFor(key)} alt={displayName(key)} className="item-img"/>
                        <span>{displayName(key)}</span>
                      </div>
                      <div className="item-row">
                        <span className="label">Cost:</span>
                        <span className="value">
                          {recipe.length
                            ? recipe.map(([r,c]) => `${c} ${r}`).join(', ')
                            : '—'}
                        </span>
                      </div>
                      <div className="item-row">
                        <span className="label">You Gain:</span>
                        <span className="value">
                          {stats.length
                            ? stats.map(([s,v]) => `${s}+${v}`).join(', ')
                            : '—'}
                        </span>
                      </div>
                      <button className="action-btn" onClick={() => craft(key)}>
                        Craft
                      </button>
                    </li>
                  )
                })}
                {craftList.length === 0 && <p className="empty">Nothing left to craft!</p>}
              </ul>
            )}

            {activeTab === 'upgrade' && (
              <ul className="craft-list">
                {upgradeList.map(([key, def]) => {
                  const ownedRaw   = inventory[def.category]?.[key] || inventory.tools?.[key]
                  const ownedStats = (ownedRaw.stats as Record<string, number>) || {}
                  const level      = ownedRaw.level || 1
                  const mult       = 2 ** level
                  const recipe     = def.baseRecipe ? Object.entries(def.baseRecipe) : []
                  const incr       = def.upgradeIncr || {}
                  const nextStats  = Object.entries(ownedStats).map(([stat, val]) => {
                    const add = incr[stat] || 0
                    return `${stat}: ${val + add}`
                  })
                  return (
                    <li key={key}>
                      <div className="item-header">
                        <img src={imgFor(key)} alt={displayName(key)} className="item-img"/>
                        <span>{displayName(key)}</span>
                      </div>
                      <div className="item-row">
                        <span className="label">Level:</span>
                        <span className="value">{level}</span>
                      </div>
                      <div className="item-row">
                        <span className="label">Upgrade Cost:</span>
                        <span className="value">
                          {recipe.length
                            ? recipe.map(([r,c]) => `${c * mult} ${r}`).join(', ')
                            : '—'}
                        </span>
                      </div>
                      <div className="item-row">
                        <span className="label">Current Stats:</span>
                        <span className="value">
                          {Object.entries(ownedStats).map(([s,v]) => `${s}: ${v}`).join(', ')}
                        </span>
                      </div>
                      <div className="item-row">
                        <span className="label">Next Stats:</span>
                        <span className="value">
                          {nextStats.length ? nextStats.join(', ') : '—'}
                        </span>
                      </div>
                      <button className="action-btn" onClick={() => upgrade(key)}>
                        Upgrade
                      </button>
                    </li>
                  )
                })}
                {upgradeList.length === 0 && <p className="empty">No upgrades available.</p>}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  )
}
